import compose from 'recompose/compose';
import get from 'lodash/get';
import find from 'lodash/find';
import isFunction from 'lodash/isFunction';
import PropTypes from 'prop-types';
import {window} from 'ssr-window';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import SearchTracking from 'utils/search-tracking';
import SpotMap from './SpotMap';
import WithConfig from 'config/WithConfig';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {GOOGLE_MAPS_LAT_LNG} from '../search-prop-types';
import {maps as gmaps} from 'google';
import {push} from 'router/router-actions';
import {resetFilters} from 'filters/filters-actions';
import {updateSelected} from 'spot/spot-actions';
import {
    setSearchMapInstance,
    setZoom,
    toggleFiltersView,
} from 'search/search-actions';

class SearchAirportMap extends Component {
    static propTypes = {
        searchRequestId: PropTypes.string,
        map: PropTypes.object,
        destination: PropTypes.shape({
            title: PropTypes.string.isRequired,
            latitude: PropTypes.number.isRequired,
            longitude: PropTypes.number.isRequired,
            position: GOOGLE_MAPS_LAT_LNG
        }).isRequired,
        ends: PropTypes.string,
        idealDistance: PropTypes.number,
        isFiltered: PropTypes.bool,
        isListViewActive: PropTypes.bool,
        kmlMapLayerEnabled: PropTypes.bool,
        kmlMapLayerURL: PropTypes.string,
        mapLoadingIncrement: PropTypes.number,
        onZoom: PropTypes.func,
        overrideIdealZoom: PropTypes.bool,
        pushTo: PropTypes.func,
        requestIncrements: PropTypes.number,
        resetFiltersClick: PropTypes.func,
        searchString: PropTypes.string,
        selectedSpot: PropTypes.object,
        setSearchMap: PropTypes.func,
        updateSelectedSpot: PropTypes.func,
        spots: PropTypes.arrayOf(PropTypes.object),
        starts: PropTypes.string,
        staticBaseUrl: PropTypes.string.isRequired,
        toggleFiltersClick: PropTypes.func,
        zoom: PropTypes.number,
    };
    static defaultProps = {
        isListViewActive: true
    };

    constructor(props) {
        super(props);

        this._isMobile = EnvironmentUtils.isMobileDevice();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            isListViewActive
        } = this.props;

        if (!this._isMobile && prevProps.isListViewActive !== isListViewActive) {
            this._transitionMap('list-view-hidden');
        }
    }

    _onMarkerClick = spotId => {
        const {
            spots,
            starts,
            ends,
            pushTo,
            updateSelectedSpot
        } = this.props;
        const spot = find(spots, {spotId});
        const {
            facility: {
                spot_details_url: spotDetailsUrl = ''
            } = {}
        } = spot;

        updateSelectedSpot({
            spot,
            referrer: 'pin'
        });

        pushTo(`/${spotDetailsUrl}/?airport=true&starts=${starts}&ends=${ends}`);
    }

    _onMapMoved = ({zoomed = false, autoZoomed = false}) => {
        SearchTracking.updateAction(
            (zoomed)
                ? (autoZoomed)
                    ? 'AUTO_ZOOM'
                    : 'ZOOM'
                : 'PAN'
        );
    }

    _noop = () => {
        // do nothing
    };

    _getPrice = (selectedRate = {}) => {
        return get(selectedRate, 'consumer_breakdown.price_per_day');
    }

    _transitionMap = toggleClassName => {
        const {
            spots,
            map
        } = this.props;
        const pageContainer = DOMUtils.el('.search-airport-page');

        DOMUtils.toggleClass(pageContainer, toggleClassName);

        window.transitionEnd(pageContainer).bind(() => {
            if (this._spotMap && isFunction(this._spotMap.triggerResize)) {
                this._spotMap.triggerResize();
            }

            if (toggleClassName === 'list-view-hidden') {
                const activeSpot = find(spots, 'activeMarker');

                if (activeSpot) {
                    map.panTo(new gmaps.LatLng(activeSpot.latitude, activeSpot.longitude));
                }
            }

            window.transitionEnd(pageContainer).unbind();
        });
    }

    render() {
        const {
            searchRequestId,
            spots,
            zoom,
            onZoom,
            isFiltered,
            destination,
            searchString,
            starts,
            ends,
            idealDistance,
            overrideIdealZoom,
            mapLoadingIncrement,
            requestIncrements,
            toggleFiltersClick,
            resetFiltersClick,
            kmlMapLayerURL,
            kmlMapLayerEnabled,
            selectedSpot,
            setSearchMap,
            staticBaseUrl
        } = this.props;

        return (
            <div className="SearchMap">
                <SpotMap
                    ref={node => { this._spotMap = node; }}
                    isMobile={this._isMobile}
                    searchRequestId={searchRequestId}
                    isFiltered={isFiltered}
                    zoom={zoom}
                    onZoom={onZoom}
                    idealDistance={idealDistance}
                    overrideIdealZoom={overrideIdealZoom}
                    destination={destination}
                    spots={spots}
                    searchString={searchString}
                    starts={starts}
                    ends={ends}
                    setSearchMap={setSearchMap}
                    mapLoadingIncrement={mapLoadingIncrement}
                    requestIncrements={requestIncrements}
                    onMarkerClick={this._onMarkerClick}
                    loadRadialSpots={this._noop}
                    loadTiledSpots={this._noop}
                    toggleFiltersClick={toggleFiltersClick}
                    resetFiltersClick={resetFiltersClick}
                    getPrice={this._getPrice}
                    kmlMapLayerURL={kmlMapLayerURL}
                    kmlMapLayerEnabled={kmlMapLayerEnabled}
                    selectedSpot={selectedSpot}
                    onMapMoved={this._onMapMoved}
                    staticBaseUrl={staticBaseUrl}
                />
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        search: {
            data: {
                currentIncrement,
                requestIncrements,
                zoom,
                overrideIdealZoom,
                destinationTitle,
                selectedSpot,
                isListVisible,
                kmlMapLayerEnabled,
                kmlMapLayerURL,
                searchMapInstance
            }
        },
        searchRequest: {
            id,
            starts,
            ends,
            latitude,
            longitude,
            search_string: searchString,
            ideal_search_distance: idealDistance
        },
        filters: {
            numActive
        }
    } = state;

    return {
        destination: {
            title: destinationTitle,
            latitude,
            longitude,
            position: new gmaps.LatLng(latitude, longitude)
        },
        ends,
        idealDistance,
        isFiltered: (numActive > 0),
        isListViewActive: isListVisible,
        kmlMapLayerEnabled,
        kmlMapLayerURL,
        map: searchMapInstance,
        mapLoadingIncrement: currentIncrement,
        overrideIdealZoom,
        requestIncrements,
        searchRequestId: id,
        searchString,
        selectedSpot,
        starts,
        zoom,
    };
};

const mapDispatchToProps = {
    onZoom: setZoom,
    resetFiltersClick: resetFilters,
    setSearchMap: setSearchMapInstance,
    updateSelectedSpot: updateSelected,
    toggleFiltersClick: toggleFiltersView,
    pushTo: push,
};

const enhance = compose(
    WithConfig,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SearchAirportMap);
